// Browser-specific help: fixing a blocked microphone, and keeping the tab
// from being put to sleep (a sleeping tab can't ring). Pure, unit-tested.

export type Browser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';

export function browserOf(userAgent: string): Browser {
  const ua = userAgent;
  if (/Edg(e|A|iOS)?\//.test(ua)) return 'edge';
  if (/Firefox\/|FxiOS\//.test(ua)) return 'firefox';
  if (/OPR\/|Opera|SamsungBrowser\//.test(ua)) return 'other';
  if (/Chrome\/|CriOS\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return 'safari';
  return 'other';
}

/** Microphone permission as the app knows it. */
export type MicState = 'unknown' | 'prompt' | 'granted' | 'denied';

/** A getUserMedia error that means "not allowed" (by the person or the system). */
export function isPermissionError(name: string): boolean {
  return name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError';
}

export const MIC_BLOCKED_TEXT = 'Microphone is blocked – calls can’t be answered.';

/** Short steps to allow the microphone again for this site. */
export function micFixSteps(b: Browser, site: string): string[] {
  const mac = 'On a Mac, also check System Settings → Privacy & Security → Microphone.';
  switch (b) {
    case 'chrome':
      return [
        `Click the icon to the left of ${site} in the address bar.`,
        'Turn Microphone on (or set it to Allow).',
        'Reload the page.',
        mac,
      ];
    case 'edge':
      return [
        `Click the lock icon to the left of ${site} in the address bar.`,
        'Choose Permissions for this site, then set Microphone to Allow.',
        'Reload the page.',
        mac,
      ];
    case 'firefox':
      return [
        'Click the crossed-out microphone (or the lock) in the address bar.',
        'Remove “Blocked” next to Use the Microphone.',
        'Reload the page, then choose Allow when Firefox asks.',
        mac,
      ];
    case 'safari':
      return [
        `In the menu bar choose Safari → Settings for ${site}…`,
        'Set Microphone to Allow.',
        'Reload the page.',
        mac,
      ];
    default:
      return [`Open this site’s settings in your browser (the icon next to ${site}) and allow the microphone.`, 'Reload the page.'];
  }
}

export const ALERTS_BLOCKED_TEXT = 'Call alerts are blocked, so you won’t see calls while CloudPhone is in the background.';

/** The browser didn't show its question (it blocked quietly): where to find it. */
export function alertQuestionHiddenText(b: Browser, site: string): string {
  if (b === 'edge') return `Edge didn’t show its question. Click the crossed-out bell in the address bar and choose Allow (or the lock icon left of ${site} → Permissions for this site → Notifications → Allow).`;
  if (b === 'chrome') return `Chrome didn’t show its question. Click the crossed-out bell in the address bar and choose Allow (or the icon left of ${site} → Notifications → Allow).`;
  return `Your browser didn’t show its question. Look for a crossed-out bell in the address bar, or open this site’s settings (the icon left of ${site}) and allow notifications.`;
}

/** Short steps to allow notifications ("call alerts") again for this site. */
export function alertFixSteps(b: Browser, site: string): string[] {
  const system = (app: string) =>
    `Still nothing? Check that notifications for ${app} are on in your computer’s settings (Windows: Settings → System → Notifications; Mac: System Settings → Notifications) and that Do Not Disturb / Focus is off.`;
  switch (b) {
    case 'chrome':
      return [
        `Click the icon to the left of ${site} in the address bar.`,
        'Turn Notifications on (or set them to Allow).',
        'Reload the page.',
        system('Google Chrome'),
      ];
    case 'edge':
      return [
        `Click the lock icon to the left of ${site} in the address bar.`,
        'Choose Permissions for this site, then set Notifications to Allow.',
        'Reload the page.',
        system('Microsoft Edge'),
      ];
    case 'firefox':
      return [
        'Click the lock (or the crossed-out bell) in the address bar.',
        'Remove “Blocked” next to Send Notifications.',
        'Reload the page, then choose Turn on and Allow when Firefox asks.',
        system('Firefox'),
      ];
    case 'safari':
      return [
        'In the menu bar choose Safari → Settings → Websites → Notifications.',
        `Set ${site} to Allow.`,
        'Reload the page.',
        system('Safari'),
      ];
    default:
      return [`Open this site’s settings in your browser (the icon next to ${site}) and allow notifications.`, 'Reload the page.'];
  }
}

/**
 * Chrome and Edge can put a tab to sleep to save memory; a sleeping tab can't
 * ring. Steps to exempt the site (null for browsers where this isn't needed).
 */
export function keepActiveTip(b: Browser, site: string): string | null {
  if (b === 'chrome') {
    return `Chrome can put tabs you haven’t used for a while to sleep to save memory, and a sleeping tab can’t ring. To stop that: Chrome menu → Settings → Performance → Always keep these sites active → Add → ${site}.`;
  }
  if (b === 'edge') {
    return `Edge can put tabs you haven’t used for a while to sleep, and a sleeping tab can’t ring. To stop that: Edge menu → Settings → System and performance → Never put these sites to sleep → Add → ${site}.`;
  }
  return null;
}
